import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaArrowRight, FaCrown, FaStar, FaRocket, FaGem, FaUser, FaBuilding, FaEnvelope, FaPhone, FaFileInvoice } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { createSapeagleOrder, resetOrderState } from "../redux/slices/sapeagleOrderSlice";
import { useDispatch, useSelector } from "react-redux";


const plans = [
  {
    name: "CRM",
    price: 499,
    description: "/user/month",
    period: "Annually",
    icon: <FaUser />,
    color: "from-sky-500 to-blue-700",
    features: [
      "Lead Management",
      "Customer Management",
      "Follow-up Tracking",
      "Quotation & Invoice",
      "Sales Dashboard",
      "Profile Setup",
      "Company Setup",
      "Reports & Analytics",
      "WhatsApp & Email Summary",
      "User Access Control",
    ],
  },
  {
    name: "CRM + Order",
    price: 699,
    description: "/user/month",
    period: "Annually",
    icon: <FaRocket />,
    color: "from-violet-500 to-purple-700",
    features: [
      "Everything in CRM",
      "Order Management",
      "Purchase Management",
      "Inventory Tracking",
      "Payment Tracking",
      "GST & Financial Reports",
      "Order Summary Dashboard",
      "Supplier Management",
      "API Integrations",
      "Business Workflow Automation",
    ],
  },
  {
    name: "CRM + Order + Service",
    price: 999,
    description: "/user/month",
    period: "Annually",
    icon: <FaCrown />,
    color: "from-amber-500 to-amber-700",
    features: [
      "Everything in CRM + Order",
      "Service Dashboard",
      "Service Request Management",
      "Complaint Management",
      "Technician Assignment",
      "Warranty & After-Sales",
      "Service Reports",
      "Priority Issue Tracking",
      "Multi-Department Workflow",
      "Priority Support",
    ],
  },
  {
    name: "Overall Plan",
    price: 1299,
    description: "/user/month",
    period: "Annually",
    icon: <FaGem />,
    color: "from-green-500 to-green-700",
    popular: true,
    features: [
      "Everything in CRM + Order + Service",
      "Advanced Business Dashboard",
      "Full ERP Workflow",
      "Multi-Department Management",
      "Priority Support",
      "Custom Reporting",
      "Automation & Integration",
      "Executive Insights",
      "Advanced Analytics",
      "Premium Training & Setup",
    ],
  },
];

const userOptions = [1, 2, 3, 5, 10, 15, 20, 25, 50];
const GST_RATE = 0.18;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", damping: 25, stiffness: 300 }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.2 }
  },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};


const PricingPage = () => {
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((state) => state.sapeagleOrder);
 
const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };

    document.body.appendChild(script);
  });
};



  const handleGetStarted = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6"
          >
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            <span className="text-blue-700 text-sm font-medium">Flexible Pricing</span>
          </motion.div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Simple, Transparent{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>

          <p className="text-xl text-gray-600 mb-4 max-w-2xl mx-auto">
            Transforming Customer Connections, One Click at a Time.
          </p>
          <p className="text-gray-500 mb-6">
            Irresistible Features Tailored for Excellence.
          </p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-4 text-sm font-medium"
          >
            <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
              <FaCheck /> Upgrade Your Plan Anytime
            </span>
            <span className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full">
              <FaStar /> Flexible Free Trial
            </span>
          </motion.div>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {plans.map((plan, index) => (
            <PricingCard
              key={index}
              plan={plan}
              index={index}
              expanded={expandedPlan === index}
              onToggle={() => setExpandedPlan(expandedPlan === index ? null : index)}
              onGetStarted={handleGetStarted}
            />
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 mb-6">Need a custom solution? Contact us for personalized pricing.</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            Request Custom Quote
            <FaArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && selectedPlan && (
          <PlanModal
            plan={selectedPlan}
            onClose={handleCloseModal}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const PricingCard = ({ plan, index, expanded, onToggle, onGetStarted }) => {
  const displayFeatures = expanded ? plan.features : plan.features.slice(0, 8);
  const remainingCount = plan.features.length - 8;

  return (
    <motion.div
      variants={itemVariants}
      className={`relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 flex flex-col ${plan.popular ? "ring-2 ring-amber-400 ring-offset-2" : ""
        }`}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2">
            <FaCrown className="w-3 h-3" /> Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className={`p-6 rounded-t-3xl bg-gradient-to-br ${plan.color} text-white`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            {plan.icon}
          </div>
          <h3 className="text-xl font-bold">{plan.name}</h3>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-extrabold">₹{plan.price}</span>
          <span className="text-white/80">{plan.description}</span>
        </div>
        <p className="text-white/70 text-sm mt-1">{plan.period}</p>
      </div>

      {/* Features List */}
      <div className="flex-1 p-6">
        <ul className="space-y-3">
          {displayFeatures.map((feature, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-3"
            >
              <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <FaCheck className="text-green-600 text-xs" />
              </span>
              <span className="text-gray-700 text-sm">{feature}</span>
            </motion.li>
          ))}

          {!expanded && remainingCount > 0 && (
            <li className="text-gray-500 text-sm italic py-2">
              +{remainingCount} more features
            </li>
          )}
        </ul>
      </div>

      {/* Actions */}
      <div className="p-6 pt-0">
        <button
          onClick={onToggle}
          className="w-full mb-3 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
        >
          {expanded ? "Show Less" : "View All Features"}
          <FaArrowRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onGetStarted(plan)}
          className={`w-full py-3.5 bg-gradient-to-r ${plan.color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2`}
        >
          Get Started
          <FaArrowRight className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const PlanModal = ({ plan, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    mobileNumber: "",
    emailAddress: "",
    gstNumber: "",
    numberOfUsers: 5,
  });
  const [billingType, setBillingType] = useState('monthly');

  const [calculatedPricing, setCalculatedPricing] = useState({
    subtotal: 0,
    gstAmount: 0,
    total: 0,
  });
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector(
    (state) => state.sapeagleOrder
  );
  const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };

    document.body.appendChild(script);
  });
};
useEffect(() => {

  const totalMonths =
    (Number(formData.years || 0) * 12) +
    Number(formData.months || 0);

  const subtotal =
    plan.price *
    Number(formData.numberOfUsers || 0) *
    totalMonths;

  const gstAmount = subtotal * GST_RATE;

  const total = subtotal + gstAmount;

  setCalculatedPricing({
    subtotal: Math.round(subtotal * 100) / 100,
    gstAmount: Math.round(gstAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
  });

}, [
  formData.numberOfUsers,
  formData.months,
  formData.years,
  plan.price
]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "numberOfUsers" ? parseInt(value, 10) : value,
    }));
  };

const handleSubmit = async (e) => {
  
  e.preventDefault();

  const res = await loadRazorpay();

  if (!res) {
    alert("Razorpay SDK Failed to Load");
    return;
  }

     const orderData = {
       fullName: formData.fullName,
       companyName: formData.companyName,
       mobileNumber: formData.mobileNumber,
       emailAddress: formData.emailAddress,
       gstNumber: formData.gstNumber,
       billingType: billingType,

       planName: plan.name,
       pricePerUser: plan.price,
       numberOfUsers: formData.numberOfUsers,

       subtotal: calculatedPricing.subtotal,
       gstAmount: calculatedPricing.gstAmount,
       totalAmount: calculatedPricing.total,

       paymentStatus: "Paid",

       paymentReference: "",

       isActive: true,
       isDeleted: false,
     };

  const options = {
    key: "rzp_test_SuLYkNJezSKMsw",

    amount: calculatedPricing.total * 100,

    currency: "INR",

    name: "SapEagle",

    description: `${plan.name} Subscription`,

    handler: async function (response) {

      orderData.paymentReference =
        response.razorpay_payment_id;

      try {

        const resultAction = await dispatch(
          createSapeagleOrder(orderData)
        );

        if (
          createSapeagleOrder.fulfilled.match(resultAction)
        ) {

          navigate("/thank-you", {
            state: {
              ...orderData,
              razorpayPaymentId:
                response.razorpay_payment_id,
            },
          });

          onClose();

          dispatch(resetOrderState());
        }

      } catch (err) {
        console.log(err);
      }
    },

    prefill: {
      name: formData.fullName,
      email: formData.emailAddress,
      contact: formData.mobileNumber,
    },

    notes: {
      company: formData.companyName,
      plan: plan.name,
    },

    theme: {
      color: "#2563eb",
    },
  };

  const paymentObject =
    new window.Razorpay(options);

  paymentObject.open();
};





  {
    error && (
      <p className="text-red-500 text-sm">
        {error?.message || "Something went wrong"}
      </p>
    )
  }
  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl shadow-2xl"
      >
        {/* Background Glow */}
        <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-10`} />

        <div className="relative bg-white rounded-3xl overflow-hidden flex flex-col max-h-[90vh]">

          {/* Beautiful Header */}
          <div className={`bg-gradient-to-br ${plan.color} p-6 lg:p-8 text-white relative flex-shrink-0`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all duration-200"
            >
              <FiX className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-4xl">
                {plan.icon}
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold">{plan.name}</h2>
                <p className="text-white/80 mt-1">Complete Business Solution</p>
              </div>
            </div>

             <div className="mt-5 flex items-baseline gap-2">
               <span className="text-4xl lg:text-5xl font-extrabold">₹{plan.price}</span>
               <span className="text-lg opacity-75">/user/month</span>
             </div>
             <p className="text-white/70 text-sm">{billingType === 'monthly' ? 'Billed monthly • Cancel anytime' : 'Billed annually • Cancel anytime'}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden flex-1">

            {/* Form Section - Smaller Inputs */}
            <div className="lg:col-span-3 p-6 lg:p-8 overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-5">
                Tell us about yourself
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaBuilding className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        placeholder="Your Company Pvt Ltd"
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        required
                        placeholder="+91 98765 43210"
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="emailAddress"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        required
                        placeholder="you@company.com"
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1.5">
                     GST Number (Optional)
                   </label>
                   <div className="relative">
                     <FaFileInvoice className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                     <input
                       type="text"
                       name="gstNumber"
                       value={formData.gstNumber}
                       onChange={handleChange}
                       placeholder="22AAAAA0000A1Z5"
                       className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                     />
                   </div>
                 </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

  {/* MONTH INPUT */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      Number of Months
    </label>

    <input
      type="number"
      min="0"
      name="months"
      value={formData.months}
      onChange={handleChange}
      placeholder="Enter Months"
      className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
    />
  </div>

  {/* YEAR INPUT */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      Number of Years
    </label>

    <input
      type="number"
      min="0"
      name="years"
      value={formData.years}
      onChange={handleChange}
      placeholder="Enter Years"
      className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
    />
  </div>

</div>

           <div>
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    Number of Users
  </label>

  <input
    type="number"
    min="1"
    name="numberOfUsers"
    value={formData.numberOfUsers}
    onChange={handleChange}
    placeholder="Enter Number of Users"
    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
  />
</div>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 mt-6 bg-gradient-to-r ${plan.color} text-white font-semibold rounded-2xl text-base shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70`}
                >
                  {loading ? "Processing..." : "Proceed to Secure Payment"}

                  {!loading && <FaArrowRight className="w-5 h-5" />}
                </motion.button>
              </form>
            </div>

            {/* Enhanced Pricing Summary */}
            <div className="lg:col-span-2 bg-gradient-to-br from-gray-50 to-slate-50 p-6 lg:p-8 border-l border-gray-100 overflow-y-auto">
              <h3 className="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
                <FaCrown className="text-amber-500" />
                Order Summary
              </h3>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-gray-600 text-sm">Plan Selected</span>
                    <span className="font-semibold text-gray-900">{plan.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-t">
                    <span className="text-gray-600 text-sm">Price per User</span>
                    <span className="font-semibold">₹{plan.price}/month</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-t">
                    <span className="text-gray-600 text-sm">Number of Users</span>
                    <span className="font-semibold text-lg">{formData.numberOfUsers}</span>
                  </div>
                </div>

                 <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
             <div className="flex justify-between text-sm">
  <span className="text-gray-600">
    Months
  </span>

  <span className="font-semibold">
    {formData.months}
  </span>
</div>

<div className="flex justify-between text-sm">
  <span className="text-gray-600">
    Years
  </span>

  <span className="font-semibold">
    {formData.years}
  </span>
</div>
                   <div className="flex justify-between text-sm">
                     <span className="text-gray-600">Subtotal</span>
                     <span className="font-semibold">₹{calculatedPricing.subtotal}</span>
                   </div>
                   <div className="flex justify-between text-sm">
                     <span className="text-gray-600">GST (18%)</span>
                     <span className="font-semibold">₹{calculatedPricing.gstAmount}</span>
                   </div>
                   <div className="pt-4 border-t border-gray-200">
                     <div className="flex justify-between items-center">
                       <span className="text-lg font-bold text-gray-900">Total Amount</span>
                       <span className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                         ₹{calculatedPricing.total}
                       </span>
                     </div>
                     <p className="text-sm text-gray-500 mt-1">Custom Billing Duration</p>
                   </div>
                 </div>

                <div className="text-center text-xs text-gray-500 pt-4">
                  🔒 Secure Payment • 30 Days Money Back Guarantee
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PricingPage;